import { useContext, useEffect, useState } from 'react';
import { useOktaAuth } from '@okta/okta-react';
import { ApiServiceContext } from './api/ApiService.context';
import { AccessService } from './api/access/AccessService.class';
import { ApiServiceCollection } from './api/ApiServiceCollection.class';

// Module-level so the check runs once per app load, surviving remounts of ProtectedRoute.
let hasVerifiedAccess = false;

export const resetAccessVerification = () => {
  hasVerifiedAccess = false;
};

export type VerifyAccessStatus = 'pending' | 'verified';

// Calls the Management API access check once the user is authenticated.
// 401/403 mean the token has no admin access: sign the user out (back to /login).
// Other failures (network, 5xx) fail open — the ticket only mandates 403, and
// signing users out on a transient backend error would be destructive.
export const useVerifyAccess = (): VerifyAccessStatus => {
  const { oktaAuth, authState } = useOktaAuth();
  const apiServiceContext = useContext(ApiServiceContext);
  const {
      AccessService,
  } = apiServiceContext.getApiServiceCollections() as {
      AccessService: AccessService;
      [key: string]: ApiServiceCollection;
  };
  const [verified, setVerified] = useState<boolean>(hasVerifiedAccess);

  const isAuthenticated = !!authState?.isAuthenticated;

  useEffect(() => {
    if (!isAuthenticated || hasVerifiedAccess) {
      return;
    }

    let cancelled = false;

    const signOut = (status: number) => {
      console.warn(`Access check returned ${status}, signing out`);
      oktaAuth.signOut().catch((err) => console.error('Sign out failed', err));
    };

    const markVerified = () => {
      hasVerifiedAccess = true;
      if (!cancelled) {
        setVerified(true);
      }
    };

    const handleResult = (resp: any) => {
      if (resp?.success) {
        markVerified();
        return;
      }
      const status = resp?.error?.status ?? resp?.response?.status;
      if (status === 401 || status === 403) {
        signOut(status);
      } else {
        console.error('Access check failed', resp);
        markVerified();
      }
    };

    AccessService.verifyAccess()
      .then(handleResult)
      .catch(handleResult);

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, oktaAuth, AccessService]);

  return verified ? 'verified' : 'pending';
};
